import { apiClient } from '@/utils/api'

export type JoinRequestStatus = 'pending' | 'approved' | 'rejected'

export interface JoinRequest {
  requestId: string
  groupId: string
  userId: string
  status: JoinRequestStatus
  message?: string
  createdAt: string
  respondedAt?: string
  respondedBy?: string
}

export interface JoinRequestWithProfile extends JoinRequest {
  displayName: string
  avatar?: string
}

export const joinRequestService = {
  /**
   * Create a join request for a group
   */
  async create(groupId: string, message?: string): Promise<JoinRequest> {
    const response = await apiClient.authPost<{ data: JoinRequest }>(
      `/groups/${groupId}/join-requests`,
      { message }
    )
    return response.data
  },

  /**
   * Get current user's join request for a group
   */
  async getMyRequest(groupId: string): Promise<JoinRequest | null> {
    const response = await apiClient.authGet<{ data: JoinRequest | null }>(
      `/groups/${groupId}/join-requests/me`
    )
    return response.data
  },

  /**
   * Cancel own join request
   */
  async cancelMyRequest(groupId: string): Promise<void> {
    await apiClient.authDelete(`/groups/${groupId}/join-requests/me`)
  },

  /**
   * List join requests for a group (admin only)
   */
  async list(groupId: string, status?: JoinRequestStatus): Promise<JoinRequestWithProfile[]> {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    
    const query = params.toString()
    const url = query ? `/groups/${groupId}/join-requests?${query}` : `/groups/${groupId}/join-requests`
    
    const response = await apiClient.authGet<{ data: JoinRequestWithProfile[]; meta: { total: number } }>(url)
    return response.data
  },

  /**
   * Get pending request count (admin only)
   */
  async countPending(groupId: string): Promise<number> {
    const response = await apiClient.authGet<{ data: { count: number } }>(
      `/groups/${groupId}/join-requests/count`
    )
    return response.data.count
  },

  /**
   * Approve a join request (admin only)
   */
  async approve(groupId: string, requestId: string): Promise<JoinRequest> {
    const response = await apiClient.authPost<{ data: JoinRequest }>(
      `/groups/${groupId}/join-requests/${requestId}/approve`,
      {}
    )
    return response.data
  },

  /**
   * Reject a join request (admin only)
   */
  async reject(groupId: string, requestId: string): Promise<JoinRequest> {
    const response = await apiClient.authPost<{ data: JoinRequest }>(
      `/groups/${groupId}/join-requests/${requestId}/reject`,
      {}
    )
    return response.data
  },
}
